/**
 * Pure H OS · Sprint 12 · Platform mocks — types + live views.
 * Payload now lives in MongoDB `pureh_*` collections.
 * `DEFAULT_PREFERENCES` is UI configuration (not business data) — kept
 * as a live singleton served by MongoDB `pureh_default_preferences`.
 */
import { liveArray, liveSingleton } from "../services/store";

// ── Notifications ────────────────────────────────────────────────
export type NotificationKind = "mention" | "priority" | "ai" | "approval" | "system";
export type Notification = {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  actor: string;
  timestamp: string;
  read: boolean;
  severity?: "high" | "medium" | "low";
  href?: string;
};
export const NOTIFICATIONS: Notification[] = liveArray<Notification>("notifications");

// ── Search index ────────────────────────────────────────────────
export type SearchScope = "department" | "report" | "approval" | "person" | "ai" | "page";
export type SearchResult = {
  id: string;
  scope: SearchScope;
  title: string;
  subtitle: string;
  href: string;
  keywords: string[];
};
export const SEARCH_INDEX: SearchResult[] = liveArray<SearchResult>("search_index");

// ── Quick actions ───────────────────────────────────────────────
export type QuickActionKind = "navigate" | "action";
export type QuickAction = {
  id: string;
  label: string;
  hint: string;
  kind: QuickActionKind;
  href?: string;
  shortcut?: string[];
};
export const QUICK_ACTIONS: QuickAction[] = liveArray<QuickAction>("quick_actions");

// ── Keyboard shortcuts registry ─────────────────────────────────
export type KeyboardShortcut = {
  keys: string[];
  label: string;
  category: "Global" | "Navigation" | "Actions" | "Editing";
};
export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = liveArray<KeyboardShortcut>("keyboard_shortcuts");

// ── Preferences defaults (singleton) ────────────────────────────
export type Language = "en" | "ar";
export type Density = "comfortable" | "compact";
export type Preferences = {
  language: Language;
  density: Density;
  notifications: {
    mentions: boolean;
    priority: boolean;
    ai: boolean;
    approvals: boolean;
    system: boolean;
  };
};
export const DEFAULT_PREFERENCES: Preferences = liveSingleton<Preferences>(
  "default_preferences",
  {
    language: "en",
    density: "comfortable",
    notifications: { mentions: true, priority: true, ai: true, approvals: true, system: false },
  },
);
